/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useRef, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { createRoot, Root } from 'react-dom/client';
import { MAPBOX_APIKEY, WAREHOUSE_IMAGES_URL } from '@constants';
import { useNavigate } from 'react-router-dom';
import { Warehouse } from '@views/WarehouseDetails/types';
import { isModifiedClick } from './modifiedClick';

// Set access token once at module level
mapboxgl.accessToken = MAPBOX_APIKEY;

const CLUSTER_MAX_ZOOM = 10;
const WAREHOUSE_IMAGE_PLACEHOLDER = '/images/warehouse_details_placeholder.png';

// Flox brand colors
const FLOX_BLUE = '#2563eb';
const FLOX_BLUE_DARK = '#1d4ed8';
const FLOX_BLUE_LIGHT = '#3b82f6';

// Inject custom styles for Mapbox popups and markers
const injectMapStyles = () => {
  const styleId = 'mapbox-custom-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Popup styling */
    .mapboxgl-popup {
      z-index: 100;
    }
    .mapboxgl-popup-content {
      padding: 0 !important;
      border-radius: 12px !important;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
      overflow: hidden;
      animation: popupFadeIn 0.2s ease-out;
    }
    .mapboxgl-popup-close-button {
      font-size: 20px;
      padding: 8px 12px;
      color: #6b7280;
      right: 4px;
      top: 4px;
      z-index: 10;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
    }
    .mapboxgl-popup-close-button:hover {
      background: #f3f4f6;
      color: #1f2937;
    }
    .mapboxgl-popup-tip {
      border-top-color: white !important;
    }
    @keyframes popupFadeIn {
      from {
        opacity: 0;
        transform: translateY(8px) scale(0.96);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    /* Custom marker styling */
    .warehouse-marker {
      cursor: pointer;
      display: block;
      text-decoration: none;
      transition: transform 0.15s ease;
    }
    .warehouse-marker:hover {
      transform: scale(1.1);
    }
    .warehouse-marker-pulse {
      animation: markerPulse 2s infinite;
    }
    @keyframes markerPulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4);
      }
      50% {
        box-shadow: 0 0 0 8px rgba(37, 99, 235, 0);
      }
    }
  `;
  document.head.appendChild(style);
};

// Call once to inject styles
if (typeof document !== 'undefined') {
  injectMapStyles();
}

interface FleetItem {
  type: string;
  count: number;
}

interface WarehouseImage {
  imageLocation: string;
  remoteName: string;
}

/**
 * Calculate fleet breakdown from warehouse fleet data
 */
function calculateFleetDetails(fleet: FleetItem[] | string | undefined) {
  const parsedFleet =
    typeof fleet === 'string' ? JSON.parse(fleet || '[]') : fleet;

  if (!Array.isArray(parsedFleet) || parsedFleet.length === 0) {
    return { vans: 0, lightDutyVehicle: 0, heavyDutyVehicle: 0 };
  }

  let vans = 0;
  let lightDutyVehicle = 0;
  let heavyDutyVehicle = 0;

  parsedFleet.forEach(({ type, count }: FleetItem) => {
    if (
      type === 'MultiAxleArticMoreThan26t' ||
      type === 'TwoAxleRigidLessThan26t' ||
      type === 'ThreeAxleRigidLessThan26t'
    ) {
      heavyDutyVehicle += count;
    } else if (
      type === 'LgvLessThan3AndHalft' ||
      type === 'VanLessThan1AndHalft'
    ) {
      vans += count;
    } else if (
      type === 'TwoAxleRigid18t' ||
      type === 'TwoAxleRigidLessThan7AndHalf5t' ||
      type === 'SpecialOrOther'
    ) {
      lightDutyVehicle += count;
    }
  });

  return { vans, lightDutyVehicle, heavyDutyVehicle };
}

/**
 * Parse warehouse images from various formats
 */
function parseWarehouseImages(images: WarehouseImage[] | string | undefined) {
  if (!images) return [];

  const imageArray = typeof images === 'string' ? JSON.parse(images) : images;

  return imageArray.map((image: WarehouseImage) => ({
    imgPath: `${WAREHOUSE_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`,
  }));
}

interface WarehousePopupProps {
  warehouse: Partial<Warehouse> & {
    images?: WarehouseImage[] | string;
    fleet?: FleetItem[] | string;
    city?: string;
    address?: string;
    maxStorageCapacity?: number;
    storageCapacityUnitOfMeasure?: string;
    palletStoragePricePerWeek?: number;
    currency?: string;
    warehouseId?: string;
  };
  detailsHref: string;
  onViewDetails: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

function WarehousePopup({
  warehouse,
  detailsHref,
  onViewDetails,
}: WarehousePopupProps) {
  const images = parseWarehouseImages(warehouse.images);
  const { vans, lightDutyVehicle, heavyDutyVehicle } = calculateFleetDetails(
    warehouse.fleet
  );
  const hasFleet = vans > 0 || lightDutyVehicle > 0 || heavyDutyVehicle > 0;

  return (
    <div className='w-72'>
      {/* Image with gradient overlay */}
      <div className='relative'>
        <img
          src={
            images.length > 0 ? images[0].imgPath : WAREHOUSE_IMAGE_PLACEHOLDER
          }
          alt='Warehouse'
          className='w-full h-36 object-cover'
        />
        <div className='absolute inset-0 bg-gradient-to-t from-black/40 to-transparent' />
        <div className='absolute bottom-3 left-3 right-3'>
          <h2 className='text-white font-semibold text-base leading-tight drop-shadow-md'>
            {warehouse.city
              ? `Warehouse in ${warehouse.city}`
              : warehouse.address || 'Warehouse'}
          </h2>
        </div>
      </div>

      {/* Content */}
      <div className='p-4 space-y-3'>
        {/* Stats Grid */}
        <div className='grid grid-cols-2 gap-3'>
          <div className='bg-gray-50 rounded-lg p-2.5'>
            <p className='text-xs text-gray-500 mb-0.5'>Capacity</p>
            <p className='text-sm font-semibold text-gray-900'>
              {warehouse.maxStorageCapacity?.toLocaleString() ?? '-'}{' '}
              <span className='font-normal text-gray-500 text-xs'>
                {warehouse.storageCapacityUnitOfMeasure?.toLowerCase() ?? ''}
              </span>
            </p>
          </div>
          <div className='bg-gray-50 rounded-lg p-2.5'>
            <p className='text-xs text-gray-500 mb-0.5'>Rate</p>
            {warehouse.palletStoragePricePerWeek ? (
              <p className='text-sm font-semibold text-gray-900'>
                {warehouse.currency?.toUpperCase() ?? '£'}
                {warehouse.palletStoragePricePerWeek}
                <span className='font-normal text-gray-500 text-xs'>/wk</span>
              </p>
            ) : (
              <p className='text-sm font-semibold text-gray-500'>PoA</p>
            )}
          </div>
        </div>

        {/* Fleet Info */}
        {hasFleet && (
          <div className='flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-lg px-3 py-2'>
            <svg
              className='w-4 h-4 text-blue-600 flex-shrink-0'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4'
              />
            </svg>
            <span>
              Fleet: {vans > 0 && `${vans} vans`}
              {vans > 0 &&
                (lightDutyVehicle > 0 || heavyDutyVehicle > 0) &&
                ', '}
              {lightDutyVehicle > 0 && `${lightDutyVehicle} LDV`}
              {lightDutyVehicle > 0 && heavyDutyVehicle > 0 && ', '}
              {heavyDutyVehicle > 0 && `${heavyDutyVehicle} HDV`}
            </span>
          </div>
        )}

        {/* CTA Button */}
        <a
          href={detailsHref}
          className='block w-full px-4 py-2.5 text-sm font-medium text-center text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm'
          onClick={onViewDetails}
        >
          View Details
        </a>
      </div>
    </div>
  );
}

export function Mapbox({
  warehouses,
  mapPosition,
  interactive = true,
  selectable = true,
  zoomLevel = 5,
  onViewportChange,
}: {
  warehouses: Warehouse[];
  mapPosition: { lat: number; lon: number } | null;
  /** mapbox-gl's native flag — when false, disables ALL map interaction
   *  (pan, zoom, drag, click). Use `selectable` if you only want to
   *  disable marker/cluster clicks. Default: true. */
  interactive?: boolean;
  /** Toggles marker/cluster click handling and hover cursor affordances.
   *  When false, pan/zoom still work but warehouse pins and cluster
   *  number labels become inert. Used by the `/warehouse-search/embed`
   *  iframe view. Default: true. */
  selectable?: boolean;
  zoomLevel?: number;
  onViewportChange?: (center: { lat: number; lon: number }, zoom: number) => void;
}) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRootsRef = useRef<Map<HTMLElement, Root>>(new Map());
  const navigate = useNavigate();

  // Memoize GeoJSON data to prevent unnecessary recalculations
  const geoJsonData = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: warehouses
        .filter((w) => w.location?.lat && w.location?.lon)
        .map((warehouse: Warehouse) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [warehouse.location.lon, warehouse.location.lat],
          },
          properties: {
            ...warehouse,
            // Stringify complex objects for GeoJSON compatibility
            images: JSON.stringify(warehouse.images),
            fleet: JSON.stringify(warehouse.fleet),
          },
        })),
    }),
    [warehouses]
  );

  /**
   * Cleanup all markers and their React roots
   */
  const cleanupMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      const popup = marker.getPopup();
      if (popup) {
        const popupElement = popup.getElement();
        if (popupElement) {
          const root = popupRootsRef.current.get(popupElement);
          if (root) {
            root.unmount();
            popupRootsRef.current.delete(popupElement);
          }
        }
        popup.remove();
      }
      marker.remove();
    });
    markersRef.current = [];
  }, []);

  /**
   * Create a popup with proper React 18 rendering
   */
  const createPopupContent = useCallback(
    (warehouse: WarehousePopupProps['warehouse']) => {
      const popupNode = document.createElement('div');
      popupNode.className = 'warehouse-popup-content';

      // Parse stringified properties back to objects
      const warehouseData = {
        ...warehouse,
        images:
          typeof warehouse.images === 'string'
            ? JSON.parse(warehouse.images)
            : warehouse.images,
        fleet:
          typeof warehouse.fleet === 'string'
            ? JSON.parse(warehouse.fleet)
            : warehouse.fleet,
      };

      const detailsHref = warehouse.warehouseId
        ? `/warehouse/${warehouse.warehouseId}`
        : '#';

      const root = createRoot(popupNode);
      root.render(
        <WarehousePopup
          warehouse={warehouseData}
          detailsHref={detailsHref}
          onViewDetails={(event) => {
            if (isModifiedClick(event)) return;
            event.preventDefault();
            if (warehouse.warehouseId) {
              navigate(`/warehouse/${warehouse.warehouseId}`);
            }
          }}
        />
      );

      return { popupNode, root };
    },
    [navigate]
  );

  /**
   * Create a custom marker element
   */
  const createMarkerElement = useCallback((warehouseId?: string) => {
    const el = document.createElement(warehouseId ? 'a' : 'div');
    el.className = 'warehouse-marker';
    if (el instanceof HTMLAnchorElement && warehouseId) {
      el.href = `/warehouse/${warehouseId}`;
      el.draggable = false;
      el.addEventListener('click', (event) => {
        if (isModifiedClick(event)) return;
        event.preventDefault();
      });
    }
    el.innerHTML = `
      <div style="
        width: 36px;
        height: 36px;
        background: linear-gradient(135deg, ${FLOX_BLUE} 0%, ${FLOX_BLUE_DARK} 100%);
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 3px solid white;
      ">
        <svg 
          style="transform: rotate(45deg); width: 16px; height: 16px; color: white;"
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/>
        </svg>
      </div>
    `;
    return el;
  }, []);

  /**
   * Add markers for unclustered points at high zoom levels
   */
  const addMarkersForPoints = useCallback(() => {
    cleanupMarkers();

    warehouses
      .filter((w) => w.location?.lat && w.location?.lon)
      .forEach((warehouse: Warehouse) => {
        const markerEl = createMarkerElement(
          selectable ? warehouse.warehouseId : undefined
        );
        if (!selectable) {
          // .warehouse-marker has cursor: pointer; override so the embed
          // doesn't signal clickability when there's nothing to click.
          markerEl.style.cursor = 'grab';
        }
        const marker = new mapboxgl.Marker({
          element: markerEl,
          anchor: 'bottom',
        }).setLngLat([warehouse.location.lon, warehouse.location.lat]);

        if (selectable) {
          const { popupNode, root } = createPopupContent(warehouse);

          const popup = new mapboxgl.Popup({
            closeOnClick: true,
            maxWidth: 'none',
            offset: [0, -18],
            className: 'warehouse-popup',
          }).setDOMContent(popupNode);

          popupRootsRef.current.set(popup.getElement()!, root);
          marker.setPopup(popup);
        }

        marker.addTo(mapRef.current!);
        markersRef.current.push(marker);
      });
  }, [
    warehouses,
    selectable,
    cleanupMarkers,
    createPopupContent,
    createMarkerElement,
  ]);

  useEffect(() => {
    if (!mapContainerRef.current) return undefined;

    // Capture refs for cleanup
    const currentPopupRoots = popupRootsRef.current;
    const currentMapRef = mapRef;

    // Clean up existing map instance
    if (currentMapRef.current) {
      cleanupMarkers();
      currentMapRef.current.remove();
      currentMapRef.current = null;
    }

    // Initialize new map
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [mapPosition?.lon ?? -2.436, mapPosition?.lat ?? 54.3781],
      zoom: zoomLevel,
      interactive,
      attributionControl: true,
      trackResize: true,
    });

    mapRef.current = map;

    // Add navigation controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      // Add GeoJSON source with clustering
      map.addSource('warehouses', {
        type: 'geojson',
        data: geoJsonData,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: 60,
      });

      // Cluster outer glow/shadow effect
      map.addLayer({
        id: 'cluster-shadow',
        type: 'circle',
        source: 'warehouses',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': FLOX_BLUE,
          'circle-opacity': 0.15,
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            28, // Small clusters
            10,
            34, // Medium clusters
            50,
            42, // Large clusters
          ],
          'circle-blur': 0.5,
        },
      });

      // Cluster circles layer
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'warehouses',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            FLOX_BLUE_LIGHT, // Light blue for small clusters (< 10)
            10,
            FLOX_BLUE, // Flox blue for medium clusters (10-50)
            50,
            FLOX_BLUE_DARK, // Dark blue for large clusters (50+)
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18, // Small clusters
            10,
            24, // Medium clusters
            50,
            32, // Large clusters
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Cluster count labels
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'warehouses',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': [
            'step',
            ['get', 'point_count'],
            12, // Small clusters
            10,
            13, // Medium clusters
            50,
            14, // Large clusters
          ],
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Unclustered points - outer ring
      // Unclustered point pulse/glow effect
      map.addLayer({
        id: 'unclustered-point-glow',
        type: 'circle',
        source: 'warehouses',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': FLOX_BLUE,
          'circle-opacity': 0.2,
          'circle-radius': 16,
          'circle-blur': 0.5,
        },
      });

      // Unclustered points - main circle
      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'warehouses',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': FLOX_BLUE,
          'circle-radius': 8,
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Unclustered points - inner dot
      map.addLayer({
        id: 'unclustered-point-inner',
        type: 'circle',
        source: 'warehouses',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 3,
        },
      });

      // Handle zoom changes - show markers at high zoom
      const handleZoom = () => {
        const currentZoom = map.getZoom();

        if (currentZoom >= CLUSTER_MAX_ZOOM) {
          addMarkersForPoints();
        } else {
          cleanupMarkers();
        }
      };

      map.on('zoomend', handleZoom);

      if (selectable) {
        // Cluster click - zoom in with smooth animation
        map.on('click', 'clusters', (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
          });

          if (!features.length) return;

          const clusterId = features[0]?.properties?.cluster_id;
          if (!clusterId) return;

          const source = map.getSource('warehouses') as mapboxgl.GeoJSONSource;
          if (!source) return;

          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            const geometry = features[0].geometry as GeoJSON.Point;
            map.flyTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom ?? CLUSTER_MAX_ZOOM,
              duration: 500,
              essential: true,
            });
          });
        });

        // Unclustered point click - show popup
        map.on('click', 'unclustered-point', (e) => {
          if (!e.features?.length) return;

          const feature = e.features[0];
          if (!feature.properties) return;

          const geometry = feature.geometry as GeoJSON.Point;
          const coordinates = geometry.coordinates.slice() as [number, number];

          // Handle world wrap
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          const { popupNode, root } = createPopupContent(
            feature.properties as WarehousePopupProps['warehouse']
          );

          const popup = new mapboxgl.Popup({
            closeOnClick: true,
            maxWidth: 'none',
            offset: [0, -8],
            className: 'warehouse-popup',
          })
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map);

          // Clean up React root when popup closes
          popup.on('close', () => {
            root.unmount();
          });
        });

        // Cursor styles
        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });

        map.on('mouseenter', 'unclustered-point', () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'unclustered-point', () => {
          map.getCanvas().style.cursor = '';
        });
      }

      // Initial check if we're already zoomed in
      if (map.getZoom() >= CLUSTER_MAX_ZOOM) {
        addMarkersForPoints();
      }

      // Report viewport changes to parent
      if (onViewportChange) {
        map.on('moveend', () => {
          const center = map.getCenter();
          const zoom = map.getZoom();
          onViewportChange({ lat: center.lat, lon: center.lng }, zoom);
        });
      }
    });

    // Cleanup function
    return () => {
      cleanupMarkers();

      // Clean up any remaining popup roots using captured ref
      currentPopupRoots.forEach((root) => {
        root.unmount();
      });
      currentPopupRoots.clear();

      if (currentMapRef.current) {
        currentMapRef.current.remove();
        currentMapRef.current = null;
      }
    };
  }, [
    warehouses,
    mapPosition,
    zoomLevel,
    interactive,
    selectable,
    geoJsonData,
    cleanupMarkers,
    createPopupContent,
    addMarkersForPoints,
    onViewportChange,
  ]);

  // Update source data when warehouses change without recreating the map
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    const source = mapRef.current.getSource(
      'warehouses'
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geoJsonData);
    }
  }, [geoJsonData]);

  // Update map center when position changes
  useEffect(() => {
    if (!mapRef.current || !mapPosition) return;

    mapRef.current.easeTo({
      center: [mapPosition.lon, mapPosition.lat],
      zoom: zoomLevel,
      duration: 500,
    });
  }, [mapPosition, zoomLevel]);

  return (
    <div
      ref={mapContainerRef}
      className='map-container w-full rounded-lg overflow-hidden'
      style={{ height: '800px' }}
    />
  );
}
